import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { useAuthStore } from "../store/auth";
import { proactiveTokenRefresh } from "../services/api";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/dashboard",
  },
  {
    path: "/login",
    name: "Login",
    component: () =>
      import(/* webpackChunkName: "login" */ "../views/LoginView.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: () =>
      import(/* webpackChunkName: "reset-password" */ "../views/ResetPasswordView.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: () =>
      import(/* webpackChunkName: "dashboard" */ "../views/DashboardView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/activity",
    name: "Activity",
    component: () =>
      import(/* webpackChunkName: "activity" */ "../views/ActivityView.vue"),
    meta: {
      requiresAuth: true,
      requiresRole: ["super_admin", "city_admin", "supervisor"],
    },
  },
  {
    path: "/devices",
    name: "Devices",
    component: () =>
      import(/* webpackChunkName: "devices" */ "../views/DevicesView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/equipment",
    name: "Equipment",
    component: () =>
      import(/* webpackChunkName: "equipment" */ "../views/EquipmentView.vue"),
    meta: { requiresAuth: true },
  },
  // Routes pour les traps (devices)
  {
    path: "/devices/add",
    name: "DeviceAdd",
    component: () =>
      import(
        /* webpackChunkName: "device-form" */ "../views/DeviceFormView.vue"
      ),
    meta: { requiresAuth: true },
  },
  // Route pour les dispositifs d'appâtage (non-Secu-Rat)
  {
    path: "/baiting-devices/add",
    name: "BaitingDeviceAdd",
    component: () =>
      import(
        /* webpackChunkName: "baiting-device-form" */ "../views/BaitingDeviceFormView.vue"
      ),
    meta: { requiresAuth: true },
  },
  // Routes pour les sensors
  {
    path: "/sensors/add",
    name: "SensorAdd",
    component: () =>
      import(
        /* webpackChunkName: "sensor-form" */ "../views/SensorFormView.vue"
      ),
    meta: { requiresAuth: true },
  },
  {
    path: "/sensors/:id",
    name: "SensorDetail",
    component: () =>
      import(
        /* webpackChunkName: "sensor-detail" */ "../views/SensorDetailView.vue"
      ),
    meta: { requiresAuth: true },
  },
  {
    path: "/map",
    name: "Map",
    component: () =>
      import(/* webpackChunkName: "map" */ "../views/MapView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/reports",
    name: "Reports",
    component: () =>
      import(/* webpackChunkName: "reports" */ "../views/ReportsView.vue"),
    meta: {
      requiresAuth: true,
      requiresRole: ["super_admin", "city_admin"],
    },
  },
  {
    path: "/reports/:id",
    name: "ReportDetails",
    component: () =>
      import(
        /* webpackChunkName: "report-details" */ "../views/ReportDetailsView.vue"
      ),
    meta: {
      requiresAuth: true,
      requiresRole: ["super_admin", "city_admin"],
    },
  },
  {
    path: "/field-reports",
    name: "FieldReports",
    component: () =>
      import(
        /* webpackChunkName: "field-reports" */ "../views/FieldReportsView.vue"
      ),
    meta: { requiresAuth: true },
  },
  {
    path: "/field-reports/new",
    name: "FieldReportNew",
    component: () =>
      import(
        /* webpackChunkName: "field-report-form" */ "../views/FieldReportFormView.vue"
      ),
    meta: { requiresAuth: true },
  },
  {
    path: "/field-reports/:id/edit",
    name: "FieldReportEdit",
    component: () =>
      import(
        /* webpackChunkName: "field-report-form" */ "../views/FieldReportFormView.vue"
      ),
    meta: { requiresAuth: true },
  },
  {
    path: "/field-reports/:id",
    name: "FieldReportDetail",
    component: () =>
      import(
        /* webpackChunkName: "field-report-detail" */ "../views/FieldReportDetailView.vue"
      ),
    meta: { requiresAuth: true },
  },
  {
    path: "/profile",
    name: "Profile",
    component: () =>
      import(/* webpackChunkName: "profile" */ "../views/ProfileView.vue"),
    meta: { requiresAuth: true },
  },
  // Routes mobiles - pages entières pour remplacer les modales
  {
    path: "/mobile/image",
    name: "MobileFullScreenImage",
    component: () =>
      import(/* webpackChunkName: "mobile-fullscreen-image" */ "../components/FullScreenImageModal.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/devices/:id",
    name: "DeviceDetail",
    component: () =>
      import(/* webpackChunkName: "device-detail" */ "../views/DeviceDetailView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/configuration",
    name: "Configuration",
    component: () =>
      import(
        /* webpackChunkName: "configuration" */ "../views/ConfigurationView.vue"
      ),
    meta: {
      requiresAuth: true,
      requiresRole: ["city_admin", "super_admin"],
    },
  },
  {
    path: "/admin/system",
    name: "AdminSystem",
    component: () =>
      import(
        /* webpackChunkName: "admin-system" */ "../views/AdminSystemView.vue"
      ),
    meta: {
      requiresAuth: true,
      requiresRole: ["super_admin"],
      requiresTenant: "admin",
    },
  },
  {
    path: "/users",
    name: "UsersList",
    component: () =>
      import(
        /* webpackChunkName: "tenant-details" */ "../views/UserListView.vue"
      ),
    meta: {
      requiresAuth: true,
      requiresRole: ["super_admin", "city_admin"],
      requiresTenant: "admin",
    },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () =>
      import(/* webpackChunkName: "not-found" */ "../views/NotFoundView.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: "Login", query: { redirect: to.fullPath } });
    return;
  }

  // Proactive token refresh: when the user has localStorage data (isAuthenticated)
  // but the access token cookie may have expired, refresh it BEFORE the page loads.
  // This prevents components from making API calls with an expired token.
  if (to.meta.requiresAuth && authStore.isAuthenticated) {
    const refreshed = await proactiveTokenRefresh();
    if (!refreshed) {
      // Refresh token is also expired, clear local state and redirect to login
      authStore.clearLocalState();
      next({ name: "Login", query: { redirect: to.fullPath } });
      return;
    }
  }

  // Detect mobile/tablet screen size
  const isMobile = window.innerWidth <= 1024;

  // Handle mobile redirections for authenticated users (non-super admin)
  if (isMobile && authStore.isAuthenticated && !authStore.isSuperAdmin) {
    // Pages interdites sur mobile
    const restrictedMobileRoutes = ['/reports', '/dashboard'];
    // Seule la liste /devices est interdite, mais /devices/add et /devices/:id (détails) sont autorisés
    const isRestrictedRoute = restrictedMobileRoutes.some(route => to.path.startsWith(route)) || 
                              (to.path === '/devices');
    
    // Pages autorisées sur mobile
    const allowedMobileRoutes = ['/map', '/profile', '/field-reports', '/mobile', '/devices/add', '/devices/', '/baiting-devices/add'];
    const isAllowedRoute = allowedMobileRoutes.some(route => to.path.startsWith(route));
    
    // Rediriger vers map si accès à une page restreinte
    if (isRestrictedRoute) {
      next({ path: '/map' });
      return;
    }
    
    // Rediriger vers map si accès à la racine
    if (to.path === '/' || to.path === '/dashboard') {
      next({ path: '/map' });
      return;
    }
    
    // Si ce n'est ni autorisé ni restreint, laisser passer (pour les pages comme login, etc.)
  }

  // Handle desktop redirections after login
  if (!isMobile && authStore.isAuthenticated && !authStore.isSuperAdmin) {
    // Pas de redirection spécifique nécessaire pour desktop
  }

  // Check if route requires specific role
  if (to.meta.requiresRole && authStore.isAuthenticated) {
    const requiredRoles = to.meta.requiresRole as string[];
    const userRole = authStore.userRole;

    if (!userRole || !requiredRoles.includes(userRole)) {
      next({ name: "Dashboard" });
      return;
    }
  }

  // Rediriger les super-admins qui accèdent à /admin vers /admin/system
  if (
    to.path !== "/admin/system" &&
    authStore.isSuperAdmin &&
    (!authStore.tenantId || authStore.tenantId == "admin")
  ) {
    next({ path: "/admin/system" });
    return;
  }

  // Rediriger les super-admins qui accèdent à la racine ou au tableau de bord
  if ((to.path === "/" || to.path === "/dashboard") && authStore.isSuperAdmin) {
    next({ path: "/admin/system" });
    return;
  }

  next();
});

export default router;
